'use client';

import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobilePortfolioOpen, setMobilePortfolioOpen] = useState(false);

  const aboutRef = useRef(null);
  const servicesRef = useRef(null);
  const portfolioRef = useRef(null);
  const aboutTimeout = useRef(null);
  const servicesTimeout = useRef(null);
  const portfolioTimeout = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (aboutRef.current && !aboutRef.current.contains(e.target)) setAboutOpen(false);
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false);
      if (portfolioRef.current && !portfolioRef.current.contains(e.target)) setPortfolioOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleScrollTo = (sectionId) => {
    setOpen(false);
    setAboutOpen(false);
    setMobileAboutOpen(false);
    navigate("/about");
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  };

  const handlePortfolioScroll = (sectionId) => {
    setOpen(false);
    setPortfolioOpen(false);
    setMobilePortfolioOpen(false);
    navigate("/portfolio");
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  };

  const dropdownItem = "w-full text-left px-4 py-2.5 text-sm text-[#495057] hover:bg-[#f8f9fa] hover:text-[#212529] transition border-b border-[#dee2e6] last:border-b-0";

  return (
    <header className="w-full border-b border-[#dee2e6] sticky top-0 z-50 bg-white">

      {/* Top Info Bar */}
      <div className="bg-[#212529] text-[#e9ecef] text-xs px-4 sm:px-6 py-2">
        <div className="flex flex-col items-center text-center space-y-1 lg:hidden">
          <p>🛠 From Rough to Perfect</p>
          <p>📍 6200 Stoneridge Mall Rd, Pleasanton, CA</p>
          <p>✉ info@ashlarbuildinggroup.com</p>
        </div>
        <div className="hidden lg:flex justify-between items-center">
          <p>🛠 From Rough to Perfect</p>
          <div className="flex items-center gap-4">
            <p>📍 6200 Stoneridge Mall Rd, Pleasanton, CA • ✉ info@ashlarbuildinggroup.com</p>
            {/* Social Icons */}
            <div className="flex items-center gap-2 ml-2">
              <a
                href="https://www.facebook.com/ashlarbuilding"
                target="_blank"
                rel="noopener noreferrer"
                className="w-6 h-6 rounded-full bg-white/10 hover:bg-[#1877F2] flex items-center justify-center transition-colors duration-200"
                aria-label="Facebook"
              >
                <FaFacebookF className="w-3 h-3 text-white" />
              </a>
              <a
                href="https://www.instagram.com/ashlarbuilding/?hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="w-6 h-6 rounded-full bg-white/10 hover:bg-[#ee2a7b] flex items-center justify-center transition-colors duration-200"
                aria-label="Instagram"
              >
                <FaInstagram className="w-3 h-3 text-white" />
              </a>
              <a
                href="https://www.linkedin.com/company/ashlar-building"
                target="_blank"
                rel="noopener noreferrer"
                className="w-6 h-6 rounded-full bg-white/10 hover:bg-[#0A66C2] flex items-center justify-center transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <FaLinkedinIn className="w-3 h-3 text-white" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="bg-white px-6 sm:px-8 py-4 shadow-md relative flex items-center">

        {/* Logo */}
        <div className="flex-1 flex justify-start ml-6">
          <Link to="/" className="flex items-center">
            <img src="/Images/logo.png" alt="Ashlar Building Group" className="h-12 sm:h-14 w-auto" />
          </Link>
        </div>

        {/* Desktop Menu */}
        <ul className="hidden lg:flex items-center gap-4 sm:gap-6 font-medium text-[#495057] text-sm sm:text-base">
          <li><Link to="/" className="hover:text-[#212529] transition">Home</Link></li>

          {/* About Us */}
          <li
            className="relative"
            ref={aboutRef}
            onMouseEnter={() => { clearTimeout(aboutTimeout.current); setAboutOpen(true); }}
            onMouseLeave={() => { aboutTimeout.current = setTimeout(() => setAboutOpen(false), 150); }}
          >
            <div className="flex items-center gap-1 cursor-pointer select-none">
              <Link to="/about" className="hover:text-[#212529] transition">About Us</Link>
              <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${aboutOpen ? "rotate-180" : ""}`} />
            </div>
            <div className={`absolute top-full left-0 pt-2 w-44 transition-all duration-200 ${aboutOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"}`}>
              <div className="bg-white border border-[#dee2e6] rounded-lg shadow-lg overflow-hidden">
                <button onClick={() => handleScrollTo("founder-section")} className={dropdownItem}>Our Founders</button>
                <button onClick={() => handleScrollTo("mission-section")} className={dropdownItem}>Mission & Vision</button>
              </div>
            </div>
          </li>

          <li><Link to="/process" className="hover:text-[#212529] transition">Our Process</Link></li>

          {/* Services */}
          <li
            className="relative"
            ref={servicesRef}
            onMouseEnter={() => { clearTimeout(servicesTimeout.current); setServicesOpen(true); }}
            onMouseLeave={() => { servicesTimeout.current = setTimeout(() => setServicesOpen(false), 150); }}
          >
            <div className="flex items-center gap-1 cursor-pointer select-none">
              <Link to="/services" className="hover:text-[#212529] transition">Services</Link>
              <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
            </div>
            <div className={`absolute top-full left-0 pt-2 w-56 transition-all duration-200 ${servicesOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"}`}>
              <div className="bg-white border border-[#dee2e6] rounded-lg shadow-lg overflow-hidden">
                <Link to="/services/custom-homes" onClick={() => setServicesOpen(false)} className={`block ${dropdownItem}`}>ADUs & New Custom Homes</Link>
                <Link to="/services/full-home-remodels" onClick={() => setServicesOpen(false)} className={`block ${dropdownItem}`}>Full Home Remodels</Link>
                <Link to="/services/hoa-commercial" onClick={() => setServicesOpen(false)} className={`block ${dropdownItem}`}>HOA & Commercial Projects</Link>
                <Link to="/services/takeover-projects" onClick={() => setServicesOpen(false)} className={`block ${dropdownItem}`}>Specialty: Takeover Projects</Link>
              </div>
            </div>
          </li>

          {/* Portfolio */}
          <li
            className="relative"
            ref={portfolioRef}
            onMouseEnter={() => { clearTimeout(portfolioTimeout.current); setPortfolioOpen(true); }}
            onMouseLeave={() => { portfolioTimeout.current = setTimeout(() => setPortfolioOpen(false), 150); }}
          >
            <div className="flex items-center gap-1 cursor-pointer select-none">
              <Link to="/portfolio" className="hover:text-[#212529] transition">Portfolio</Link>
              <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${portfolioOpen ? "rotate-180" : ""}`} />
            </div>
            <div className={`absolute top-full left-0 pt-2 w-56 transition-all duration-200 ${portfolioOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"}`}>
              <div className="bg-white border border-[#dee2e6] rounded-lg shadow-lg overflow-hidden">
                <button onClick={() => handlePortfolioScroll("section-adu-projects")} className={dropdownItem}>ADU Projects</button>
                <button onClick={() => handlePortfolioScroll("section-full-home-remodels")} className={dropdownItem}>Full Home Remodels</button>
                <button onClick={() => handlePortfolioScroll("section-hoa-commercial")} className={dropdownItem}>HOA & Commercial Projects</button>
                <button onClick={() => handlePortfolioScroll("section-behind-the-scenes")} className={dropdownItem}>Behind the Scenes</button>
                <button onClick={() => handlePortfolioScroll("section-videos")} className={dropdownItem}>Project Videos</button>
              </div>
            </div>
          </li>

          <li><Link to="/contact" className="hover:text-[#212529] transition">Contact Us</Link></li>
        </ul>

        {/* CTA Button */}
        <div className="hidden lg:flex flex-1 justify-end">
          <Link to="/contact">
            <button className="bg-[#343a40] hover:bg-[#212529] text-white uppercase px-5 sm:px-6 py-2 sm:py-2.5 rounded-md font-semibold text-sm sm:text-base transition">
              Schedule Consultation →
            </button>
          </Link>
        </div>

        {/* Hamburger */}
        <button className="lg:hidden text-[#212529] text-2xl ml-4" onClick={() => setOpen(!open)}>☰</button>
      </nav>

      {/* Mobile Menu */}
      <div className={`lg:hidden bg-white px-4 sm:px-6 font-medium text-[#495057] transition-all duration-300 overflow-hidden ${open ? "max-h-[900px] py-4" : "max-h-0"}`}>
        <Link to="/" onClick={() => setOpen(false)} className="block py-2 border-b border-[#dee2e6]">Home</Link>

        {/* Mobile About */}
        <div className="border-b border-[#dee2e6]">
          <div className="flex items-center justify-between py-2">
            <Link to="/about" onClick={() => setOpen(false)} className="flex-1">About Us</Link>
            <button onClick={() => setMobileAboutOpen(!mobileAboutOpen)} className="p-1">
              <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileAboutOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
          {mobileAboutOpen && (
            <div className="pl-4 pb-2 flex flex-col gap-1">
              <button onClick={() => handleScrollTo("founder-section")} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ Our Founders</button>
              <button onClick={() => handleScrollTo("mission-section")} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ Mission & Vision</button>
            </div>
          )}
        </div>

        <Link to="/process" onClick={() => setOpen(false)} className="block py-2 border-b border-[#dee2e6]">Our Process</Link>

        {/* Mobile Services */}
        <div className="border-b border-[#dee2e6]">
          <div className="flex items-center justify-between py-2">
            <Link to="/services" onClick={() => setOpen(false)} className="flex-1">Services</Link>
            <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className="p-1">
              <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
          {mobileServicesOpen && (
            <div className="pl-4 pb-2 flex flex-col gap-1">
              <Link to="/services/custom-homes" onClick={() => setOpen(false)} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ ADUs & Ground-Up Custom Homes</Link>
              <Link to="/services/full-home-remodels" onClick={() => setOpen(false)} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ Full Home Remodels</Link>
              <Link to="/services/hoa-commercial" onClick={() => setOpen(false)} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ HOA & Commercial Projects</Link>
              <Link to="/services/takeover-projects" onClick={() => setOpen(false)} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ Specialty: Takeover Projects</Link>
            </div>
          )}
        </div>

        {/* Mobile Portfolio */}
        <div className="border-b border-[#dee2e6]">
          <div className="flex items-center justify-between py-2">
            <Link to="/portfolio" onClick={() => setOpen(false)} className="flex-1">Portfolio</Link>
            <button onClick={() => setMobilePortfolioOpen(!mobilePortfolioOpen)} className="p-1">
              <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobilePortfolioOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
          {mobilePortfolioOpen && (
            <div className="pl-4 pb-2 flex flex-col gap-1">
              <button onClick={() => handlePortfolioScroll("section-adu-projects")} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ ADU Projects</button>
              <button onClick={() => handlePortfolioScroll("section-full-home-remodels")} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ Full Home Remodels</button>
              <button onClick={() => handlePortfolioScroll("section-hoa-commercial")} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ HOA & Commercial Projects</button>
              <button onClick={() => handlePortfolioScroll("section-behind-the-scenes")} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ Behind the Scenes</button>
              <button onClick={() => handlePortfolioScroll("section-videos")} className="text-left py-1.5 text-sm text-[#6c757d] hover:text-[#212529] transition">→ Project Videos</button>
            </div>
          )}
        </div>

        <Link to="/contact" onClick={() => setOpen(false)} className="block py-2 border-b border-[#dee2e6]">Contact Us</Link>

        {/* Mobile Social Icons */}
        <div className="flex items-center gap-3 py-3 border-b border-[#dee2e6]">
          <a href="https://www.facebook.com/ashlarbuilding" target="_blank" rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-[#f8f9fa] border border-[#dee2e6] hover:bg-[#1877F2] hover:border-[#1877F2] flex items-center justify-center transition-colors duration-200 group"
            aria-label="Facebook">
            <FaFacebookF className="w-3.5 h-3.5 text-[#495057] group-hover:text-white transition-colors" />
          </a>
          <a href="https://www.instagram.com/ashlarbuilding/?hl=en" target="_blank" rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-[#f8f9fa] border border-[#dee2e6] hover:bg-[#ee2a7b] hover:border-[#ee2a7b] flex items-center justify-center transition-colors duration-200 group"
            aria-label="Instagram">
            <FaInstagram className="w-3.5 h-3.5 text-[#495057] group-hover:text-white transition-colors" />
          </a>
          <a href="https://www.linkedin.com/company/ashlar-building" target="_blank" rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-[#f8f9fa] border border-[#dee2e6] hover:bg-[#0A66C2] hover:border-[#0A66C2] flex items-center justify-center transition-colors duration-200 group"
            aria-label="LinkedIn">
            <FaLinkedinIn className="w-3.5 h-3.5 text-[#495057] group-hover:text-white transition-colors" />
          </a>
        </div>

        <Link to="/contact" onClick={() => setOpen(false)}>
          <button className="w-full bg-[#343a40] hover:bg-[#212529] text-white py-2.5 rounded-md font-semibold mt-3 transition">
            Schedule Consultation →
          </button>
        </Link>
      </div>
    </header>
  );
};

export default Navbar;